import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { translations } from '../data/mock';

const LanguageContext = createContext();

// Function to detect user's country based on IP
const detectCountryByIP = async () => {
  try {
    // Try using ipapi.co (free tier: 1000 requests/day)
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });
    
    if (response.ok) {
      const data = await response.json();
      return data.country_code || null;
    }
  } catch (error) {
    // Silently fail - use default language
  }
  
  // Fallback: try ip-api.com (free tier: 45 requests/minute)
  try {
    const response = await fetch('https://ip-api.com/json/?fields=countryCode', {
      method: 'GET',
    });
    
    if (response.ok) {
      const data = await response.json();
      return data.countryCode || null;
    }
  } catch (error) {
    // Silently fail - use default language
  }
  
  return null;
};

// Function to get initial language
const getInitialLanguage = async () => {
  // Check if user has previously selected a language
  const savedLanguage = localStorage.getItem('hitech-group-language');
  if (savedLanguage === 'en' || savedLanguage === 'fr') {
    return savedLanguage;
  }
  
  // Detect country by IP
  const countryCode = await detectCountryByIP();
  
  // If France, use French; otherwise use English
  if (countryCode === 'FR') {
    return 'fr';
  }
  
  return 'en';
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [isLanguageDetected, setIsLanguageDetected] = useState(false);

  // Detect language on mount
  useEffect(() => {
    const initializeLanguage = async () => {
      const detectedLanguage = await getInitialLanguage();
      setLanguage(detectedLanguage);
      localStorage.setItem('hitech-group-language', detectedLanguage);
      setIsLanguageDetected(true);
    };
    
    initializeLanguage();
  }, []);

  // Update language handler to save to localStorage
  const updateLanguage = useCallback((newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem('hitech-group-language', newLanguage);
  }, []);

  const toggleLanguage = useCallback(() => {
    updateLanguage(language === 'en' ? 'fr' : 'en');
  }, [language, updateLanguage]);

  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage: updateLanguage, 
      toggleLanguage, 
      t,
      isLanguageDetected 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
